import React from 'react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/ru';
import { createAppTheme } from '../theme';
import { messages } from '../i18n/messages';
import { AppUiContext } from './appUiContext';

const STORAGE_THEME = 'uma-adm-theme';
const STORAGE_LOCALE = 'uma-adm-locale';

/** @typedef {'light' | 'dark'} ThemeMode */
/** @typedef {'en' | 'ru'} Locale */

function readStoredTheme() {
  try {
    const v = localStorage.getItem(STORAGE_THEME);
    if (v === 'light' || v === 'dark') return v;
  } catch {
    // ignore
  }
  return 'light';
}

function readStoredLocale() {
  try {
    const v = localStorage.getItem(STORAGE_LOCALE);
    if (v === 'en' || v === 'ru') return v;
  } catch {
    // ignore
  }
  return 'en';
}

/**
 * @param {unknown} tree
 * @param {string} path dot-separated
 * @returns {string | undefined}
 */
function lookup(tree, path) {
  const parts = path.split('.');
  let cur = tree;
  for (const p of parts) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = cur[p];
  }
  return typeof cur === 'string' ? cur : undefined;
}

/**
 * @param {string} template
 * @param {Record<string, string | number | undefined> | undefined} vars
 */
function interpolate(template, vars) {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, key) => {
    const v = vars[key];
    return v === undefined || v === null ? '' : String(v);
  });
}

export function AppUiProvider({ children }) {
  const [themeMode, setThemeModeState] = React.useState(readStoredTheme);
  const [locale, setLocaleState] = React.useState(readStoredLocale);

  const setThemeMode = React.useCallback((mode) => {
    setThemeModeState(mode);
    try {
      localStorage.setItem(STORAGE_THEME, mode);
    } catch {
      // ignore
    }
  }, []);

  const setLocale = React.useCallback((next) => {
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_LOCALE, next);
    } catch {
      // ignore
    }
    dayjs.locale(next === 'ru' ? 'ru' : 'en');
  }, []);

  React.useEffect(() => {
    dayjs.locale(locale === 'ru' ? 'ru' : 'en');
  }, [locale]);

  React.useEffect(() => {
    document.documentElement.lang = locale === 'ru' ? 'ru' : 'en';
  }, [locale]);

  const theme = React.useMemo(() => createAppTheme(themeMode), [themeMode]);

  const t = React.useCallback(
    (key, vars) => {
      const table = messages[locale] || messages.en;
      const str = lookup(table, key) ?? lookup(messages.en, key) ?? key;
      return interpolate(str, vars);
    },
    [locale]
  );

  const value = React.useMemo(
    () => ({
      themeMode,
      setThemeMode,
      locale,
      setLocale,
      t,
    }),
    [themeMode, setThemeMode, locale, setLocale, t]
  );

  return (
    <AppUiContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale={locale === 'ru' ? 'ru' : 'en'}
        >
          {children}
        </LocalizationProvider>
      </ThemeProvider>
    </AppUiContext.Provider>
  );
}
